import type { CatalogProcedureOption } from '@/features/catalog/api'
import { fieldErrors } from '@/lib/errors'
import type { PaymentTerms, Procedure, ProcedureInput } from './api'
import { EMPTY_PAYMENT_TERMS, formatPercent } from './payment-terms'

/**
 * O formulário do procedimento da clínica (docs/areas/administracao.md, "Criar / editar
 * procedimento"; RN-ADM-30 a 35, 70, 73; T47). O procedimento é a oferta da clínica: nome, valor e
 * condição de pagamento dela, ligado a um ou mais procedimentos da lista da Westhetics, que trazem
 * os documentos. Ninguém escolhe o tipo: um item com uma aplicação é avulso, o resto é clube ou
 * pacote. O nome nasce dos itens escolhidos e a clínica edita. Serve ao cadastro
 * (`procedure-page.tsx`) e à etapa Procedimentos da implantação. Os blocos ficam em
 * `procedure-form.tsx`.
 */

export const PROCEDURE_NAME_LIMIT = 80

export interface ProcedureItemState {
  id: string
  name: string
  quantity: number
  documentCount: number | null
  hasConsentForm: boolean | null
}

export interface ProcedureFormState {
  items: ProcedureItemState[]
  validityMonths: string
  name: string
  /** A pessoa escreveu o nome: ele deixa de acompanhar os itens escolhidos. */
  nameTouched: boolean
  description: string
  amountCents: number | null
  paymentTerms: PaymentTerms
  discountText: string
}

export const EMPTY_PROCEDURE_FORM: ProcedureFormState = {
  items: [],
  validityMonths: '',
  name: '',
  nameTouched: false,
  description: '',
  amountCents: null,
  paymentTerms: EMPTY_PAYMENT_TERMS,
  discountText: '',
}

/**
 * O nome automático (T47), igual ao da API: o nome do item, ou os nomes ligados por " + "; acima de
 * 80 caracteres, "<primeiro> e mais N".
 */
export function autoProcedureName(names: string[]): string {
  const clean = names.map((name) => name.trim()).filter(Boolean)
  if (clean.length === 0) return ''
  const joined = clean.join(' + ')
  if (joined.length <= PROCEDURE_NAME_LIMIT) return joined
  const suffix = ` e mais ${clean.length - 1}`
  return `${clean[0]!.slice(0, PROCEDURE_NAME_LIMIT - suffix.length).trimEnd()}${suffix}`
}

/** O desconto como a pessoa digita ("10", "7,5"), a partir da condição salva. */
export function discountTextOf(paymentTerms: PaymentTerms | null): string {
  return paymentTerms?.upfrontDiscount
    ? formatPercent(paymentTerms.upfrontDiscount.percent).replace('%', '')
    : ''
}

/** Um formulário vazio que já traz uma condição de pagamento (a do último procedimento, na implantação). */
export function emptyProcedureForm(paymentTerms: PaymentTerms | null = null): ProcedureFormState {
  return {
    ...EMPTY_PROCEDURE_FORM,
    paymentTerms: paymentTerms ?? EMPTY_PAYMENT_TERMS,
    discountText: discountTextOf(paymentTerms),
  }
}

export function procedureFormFrom(procedure: Procedure): ProcedureFormState {
  return {
    items: procedure.items.map((item) => ({
      id: item.catalogProcedure.id,
      name: item.catalogProcedure.name,
      quantity: item.quantity,
      documentCount: null,
      hasConsentForm: null,
    })),
    validityMonths: procedure.validityMonths ? String(procedure.validityMonths) : '',
    name: procedure.name,
    nameTouched: true,
    description: procedure.description ?? '',
    amountCents: procedure.amountCents,
    paymentTerms: procedure.paymentTerms ?? EMPTY_PAYMENT_TERMS,
    discountText: discountTextOf(procedure.paymentTerms),
  }
}

/** Os itens escolhidos na lista; o nome acompanha a escolha até a pessoa escrever outro. */
export function withChosenItems(
  form: ProcedureFormState,
  options: CatalogProcedureOption[],
): ProcedureFormState {
  const items = options.map((option) => ({
    id: option.id,
    name: option.name,
    quantity: form.items.find((item) => item.id === option.id)?.quantity ?? 1,
    documentCount: option.documentCount,
    hasConsentForm: option.hasConsentForm,
  }))
  return {
    ...form,
    items,
    name: form.nameTouched ? form.name : autoProcedureName(items.map((item) => item.name)),
  }
}

export type ProcedureFormErrors = Partial<
  Record<
    'items' | 'name' | 'amountCents' | 'validityMonths' | 'methods' | 'upfrontDiscount',
    string
  >
>

export function validateProcedureForm(form: ProcedureFormState): ProcedureFormErrors {
  const errors: ProcedureFormErrors = {}
  if (form.items.length === 0) {
    errors.items = 'Escolha pelo menos um procedimento da lista da Westhetics.'
  } else if (form.items.length > 20) {
    errors.items =
      'Um procedimento da clínica junta no máximo 20 procedimentos da lista da Westhetics.'
  }
  const name = form.name.trim()
  if (name.length < 2) errors.name = 'Dê um nome ao procedimento.'
  else if (name.length > PROCEDURE_NAME_LIMIT) {
    errors.name = 'O nome pode ter no máximo 80 caracteres.'
  } else if (name.includes(',')) {
    errors.name = 'Sem vírgulas: elas separam procedimentos no documento gerado.'
  }
  if (form.amountCents === null) errors.amountCents = 'Informe um valor em reais.'
  if (form.validityMonths.trim() !== '') {
    const months = Number(form.validityMonths)
    if (!Number.isInteger(months) || months < 1 || months > 60) {
      errors.validityMonths = 'A vigência vai de 1 a 60 meses.'
    }
  }
  if (form.paymentTerms.methods.length === 0) {
    errors.methods = 'Escolha pelo menos um meio de pagamento.'
  }
  if (form.discountText.trim() !== '' && !form.paymentTerms.upfrontDiscount) {
    errors.upfrontDiscount = 'Informe o desconto em porcentagem, como 10 ou 7,5.'
  }
  return errors
}

export function procedureInput(form: ProcedureFormState): ProcedureInput {
  return {
    name: form.name.trim(),
    description: form.description.trim() || null,
    items: form.items.map((item) => ({ catalogProcedureId: item.id, quantity: item.quantity })),
    validityMonths: form.validityMonths.trim() !== '' ? Number(form.validityMonths) : null,
    amountCents: form.amountCents ?? 0,
    paymentTerms: form.paymentTerms,
  }
}

/** Os erros por campo que a API devolve (422), na chave que o formulário usa. */
export function procedureServerErrors(error: unknown): ProcedureFormErrors {
  const errors: ProcedureFormErrors = {}
  for (const [key, message] of Object.entries(fieldErrors(error))) {
    if (key.startsWith('items')) errors.items = message
    else if (key === 'name') errors.name = message
    else if (key === 'amountCents') errors.amountCents = message
    else if (key === 'validityMonths') errors.validityMonths = message
    else if (key.startsWith('paymentTerms.upfrontDiscount')) errors.upfrontDiscount = message
    else if (key.startsWith('paymentTerms')) errors.methods = message
  }
  return errors
}
